/**
 * SystemHealthPage — PUBLIC, read-only Platform Constitution dashboard (June 2026).
 *
 * The pre-session governance dashboard. Fetches GET /api/system/health, which
 * evaluates every architectural invariant declared in PLATFORM_CONSTITUTION.json
 * (the same evaluator the green-gate test_platform_constitution.py uses) and shows
 * a green badge for each passing invariant, red for any violation. No auth, no PHI.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ShieldCheck, ShieldAlert, CircleCheck, CircleX, CircleHelp,
  RefreshCw, Boxes, FileJson,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pass: { dot: 'bg-emerald-500', text: 'text-emerald-400', ring: 'border-emerald-500/40 bg-emerald-500/5', Icon: CircleCheck, label: 'PASS' },
  fail: { dot: 'bg-red-500', text: 'text-red-400', ring: 'border-red-500/50 bg-red-500/10', Icon: CircleX, label: 'VIOLATION' },
  unknown: { dot: 'bg-amber-500', text: 'text-amber-400', ring: 'border-amber-500/40 bg-amber-500/5', Icon: CircleHelp, label: 'UNKNOWN' },
};

const fmt = (v) => {
  if (v === null || v === undefined) return '\u2014';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

export default function SystemHealthPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setError(false);
    try {
      const res = await axios.get(`${API}/system/health`);
      setData(res.data);
    } catch (e) {
      setError(true);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || { total: 0, passed: 0, failed: 0, unknown: 0, all_green: false };
  const allGreen = summary.all_green;

  return (
    <div className="min-h-screen bg-slate-900" data-testid="system-health-page">
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="flex items-center gap-3">
          {allGreen
            ? <ShieldCheck className="h-8 w-8 text-emerald-400" data-testid="health-shield-green" />
            : <ShieldAlert className="h-8 w-8 text-amber-400" data-testid="health-shield-alert" />}
          <h1 className="text-2xl font-bold text-white sm:text-3xl">Platform Constitution — System Health</h1>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-slate-300">
          Read-only governance dashboard. Every architectural invariant in the Platform Constitution
          is evaluated live against the running system &mdash; the same checks enforced in every
          green-gate by <code className="rounded bg-slate-800 px-1 text-teal-300">test_platform_constitution.py</code>.
          This page is public and never reveals patient information.
        </p>

        {data && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-400" data-testid="health-meta">
            <FileJson className="h-3.5 w-3.5" />
            <span>PLATFORM_CONSTITUTION.json &middot; v{data.version}</span>
            <span className="text-slate-600">&middot;</span>
            <span>{data.status}</span>
          </div>
        )}

        {/* Summary */}
        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="health-summary">
          <SummaryTile label="Invariants" value={summary.total} tone="slate" testid="summary-total" />
          <SummaryTile label="Passing" value={summary.passed} tone="emerald" testid="summary-passed" />
          <SummaryTile label="Violations" value={summary.failed} tone="red" testid="summary-failed" />
          <SummaryTile label="Unknown" value={summary.unknown} tone="amber" testid="summary-unknown" />
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${
              allGreen ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                       : 'border-amber-500/40 bg-amber-500/10 text-amber-300'}`}
            data-testid="health-overall-badge"
          >
            <Boxes className="h-3.5 w-3.5" />
            {allGreen ? 'All invariants green' : `${summary.failed} violation(s) / ${summary.unknown} unknown`}
          </div>
          <button
            onClick={load}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-md bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
            data-testid="health-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} /> Re-check
          </button>
        </div>

        {error && (
          <div className="mt-6 rounded-xl border border-red-500 bg-red-500/10 p-4 text-sm text-red-300" data-testid="health-error">
            Could not reach the health endpoint. Try Re-check.
          </div>
        )}

        {busy && !data && (
          <p className="mt-8 text-sm text-slate-400" data-testid="health-loading">Evaluating invariants&hellip;</p>
        )}

        {/* Invariant list */}
        <div className="mt-6 space-y-3" data-testid="health-invariant-list">
          {(data?.invariants || []).map((inv) => {
            const m = STATUS_META[inv.status] || STATUS_META.unknown;
            return (
              <div
                key={inv.check_id}
                className={`rounded-xl border p-4 ${m.ring}`}
                data-testid={`invariant-${inv.check_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <m.Icon className={`mt-0.5 h-5 w-5 shrink-0 ${m.text}`} />
                    <div>
                      <p className="text-sm font-semibold text-white">{inv.name}</p>
                      <p className="mt-1 text-xs leading-relaxed text-slate-400">{inv.detail}</p>
                    </div>
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${m.text}`}
                    data-testid={`invariant-status-${inv.check_id}`}
                  >
                    {m.label}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 pl-8 text-xs">
                  <div className="rounded-lg bg-slate-900/60 p-2">
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">Assertion</p>
                    <p className="font-mono text-slate-300">{inv.assertion_type}</p>
                  </div>
                  <div className="rounded-lg bg-slate-900/60 p-2">
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">Expected / Actual</p>
                    <p className="truncate font-mono text-slate-300" title={`${fmt(inv.expected)} / ${fmt(inv.actual)}`}>
                      {fmt(inv.expected)} <span className="text-slate-600">&rarr;</span> {fmt(inv.actual)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-8 text-center text-xs text-slate-500">
          Determinism is the moat. Every invariant here is mechanically enforced &mdash; the build cannot
          go green if any one of them is violated.
        </p>
      </div>
    </div>
  );
}

const TONE = {
  slate: 'text-slate-200',
  emerald: 'text-emerald-400',
  red: 'text-red-400',
  amber: 'text-amber-400',
};

function SummaryTile({ label, value, tone, testid }) {
  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800 p-4 text-center" data-testid={testid}>
      <p className={`text-3xl font-extrabold ${TONE[tone]}`}>{value}</p>
      <p className="mt-1 text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
    </div>
  );
}
